import PropTypes from "prop-types";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { useFormik, Form, FormikProvider } from "formik";
// material
import {
  Card,
  Alert,
  TextField,
  Stack,
  CardHeader,
  Box,
  Typography,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import useAuth from "../../../hooks/useAuth";
import { CheckPerm, NumberFormatCustom } from "../../../utils/apis";
import { fNumber } from "../../../utils/formatNumber";
import { fDate } from "../../../utils/formatTime";
// ----------------------------------------------------------------------
ProductExpiration.propTypes = {
  myProfile: PropTypes.object,
  uid: PropTypes.string,
  fetch: PropTypes.func,
};
export default function ProductExpiration({ myProfile, uid, fetch }) {
  const { updateProductExpiration } = useAuth();
  const hasPermission = CheckPerm("update_products");
  const { enqueueSnackbar } = useSnackbar();

  const Schema = Yup.object().shape({});
  const formik = useFormik({
    initialValues: {
      expiration: { quantity: "", date: "" },
      expirations: myProfile.expirations
        ? JSON.parse(myProfile.expirations)
        : [],
    },
    validationSchema: Schema,
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      try {
        const isEmpty = values.expirations.filter(
          (expiration) => expiration.quantity === "" || expiration.date === "",
        );
        if (isEmpty.length > 0) {
          throw new Error(
            "Please add atleast one expiration detail to proceed.",
          );
        }
        await updateProductExpiration(values.expirations, uid);
        enqueueSnackbar(`Product's expiration details has been updated!`, {
          variant: "success",
        });
        setSubmitting(false);
        fetch();
      } catch (error) {
        setErrors({ afterSubmit: error.message });
        setSubmitting(false);
        window.scrollTo(0, 0);
      }
    },
  });

  const {
    errors,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    values,
    setFieldValue,
  } = formik;

  return (
    <Card
      variant="outlined"
      sx={{
        border: "0.4px solid",
        borderColor: "background.border",
        boxShadow:
          "4px 2px 124px rgba(46, 53, 58, 0.02), 0px 4px 124px rgba(46, 41, 78, 0.02)",
        borderRadius: "8px",
        px: 3,
        pb: 3,
      }}
    >
      <CardHeader title="Expiration Dates & Quantities (optional)" />
      <Box sx={{ p: { md: 3, xs: 1 } }}>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack direction="column" spacing={0}>
              <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                You can specify the expiration dates for your products as well
                as the quantities. You will be notified of the expiring
                quantities based on the dates specified.
              </Typography>
              {errors.afterSubmit && (
                <Alert severity="error">{errors.afterSubmit}</Alert>
              )}
              <TableContainer sx={{ width: 1, maxWidth: 1, py: 4 }}>
                <Table>
                  <TableHead
                    sx={{
                      borderBottom: (theme) =>
                        `solid 1px ${theme.palette.divider}`,
                      "& th": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell align="left">Expiration Date</TableCell>
                      <TableCell align="left" width={80}>
                        Qty
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {values.expirations.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          borderBottom: (theme) =>
                            `solid 1px ${theme.palette.divider}`,
                        }}
                      >
                        <TableCell align="left">{fDate(row.date)}</TableCell>
                        <TableCell align="left">
                          {fNumber(row.quantity)}
                        </TableCell>

                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={() =>
                              setFieldValue(
                                "expirations",
                                values.expirations.filter(
                                  (item, i) => i !== index,
                                ),
                              )
                            }
                          >
                            DELETE
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack spacing={2} sx={{ mt: 2 }}>
                <Stack direction={{ md: "row", xs: "column" }} spacing={2}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    type="number"
                    {...getFieldProps("expiration.quantity")}
                  />
                  <TextField
                    fullWidth
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Expiration Date"
                    {...getFieldProps("expiration.date")}
                    helperText="This is the expiration date for the quantity specified."
                  />
                </Stack>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    if (
                      values.expiration.date &&
                      values.expiration.quantity &&
                      parseInt(values.expiration.quantity, 10) > 0
                    ) {
                      setFieldValue("expirations", [
                        ...values.expirations,
                        values.expiration,
                      ]);
                      setFieldValue("expiration", {
                        quantity: "",
                        date: "",
                      });
                    } else {
                      enqueueSnackbar(
                        "Please provide the quantity and expiration date to proceed.",
                        {
                          variant: "warning",
                        },
                      );
                    }
                  }}
                >
                  Add
                </Button>
              </Stack>
            </Stack>
            {hasPermission && (
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                sx={{ mt: 5 }}
              >
                Update
              </LoadingButton>
            )}
          </Form>
        </FormikProvider>
      </Box>
    </Card>
  );
}
